import { z } from 'zod';

/**
 * Per-scenario rank assignment · controls which rank counter increments
 * via `practiceStore.getRankStats()` longest-prefix-first lookup.
 *
 * Block 1 (existing): detective-{junior|senior|master} · author-{apprentice|senior|master}.
 * Block 2 (W3.1 extension 2026-05-04): api-master-* · sql-detective-* · security-watcher-*.
 * Block 3 (W3.1 extension 2026-05-04 · Q2=A): automation-architect-* · ci-wrangler-*.
 * Block 4 (Wave 4 future): ai-qa-master-*.
 *
 * Tier suffixes mirror BadgeDefinition IDs in `badge-catalog.ts` so a single
 * scenario can be inspected by both engine (counter increment) and UI (which
 * rank chip to highlight on completion ceremony).
 */
export const PracticeRankSchema = z.enum([
  // Block 1 detective + author
  'detective-junior',
  'detective-senior',
  'detective-master',
  'author-apprentice',
  'author-senior',
  'author-master',
  // Block 2 (W3.1 backfill)
  'api-master-junior',
  'api-master-senior',
  'api-master-master',
  'sql-detective-junior',
  'sql-detective-senior',
  'sql-detective-master',
  'security-watcher-junior',
  'security-watcher-senior',
  // Block 3 (W3.1 + Q2=A 2 ranks)
  'automation-architect-junior',
  'automation-architect-senior',
  'automation-architect-master',
  'ci-wrangler-junior',
  'ci-wrangler-senior',
  // Block 4 (Wave 4 future · listed для forward-compat schema)
  'ai-qa-master-junior',
  'ai-qa-master-senior',
  'ai-qa-master-master',
]);

export const PracticeProgressSchema = z.object({
  scenarioId: z.string(),
  startedAt: z.number(),
  completedAt: z.number().optional(),
  attempts: z.number().int(),
  hintsRevealed: z.array(z.number().int()),
  finalScore: z.number(),
  rank: PracticeRankSchema.optional(),
});

export const AuthorDraftSchema = z.object({
  scenarioId: z.string(),
  text: z.string(),
  rubricSelfGrade: z.record(z.string(), z.boolean()),
  breakdownRevealed: z.boolean(),
  lastSavedAt: z.number(),
});

export type PracticeRank = z.infer<typeof PracticeRankSchema>;
export type PracticeProgress = z.infer<typeof PracticeProgressSchema>;
export type AuthorDraft = z.infer<typeof AuthorDraftSchema>;
